import React from "react";
import { Brain, Loader2, RefreshCw } from "lucide-react";

const AI_NOTES_LABELS = [
  ["tipo_siniestro", "Tipo de siniestro"],
  ["ramo", "Ramo"],
  ["objeto_asegurado", "Objeto asegurado"],
  ["estancia", "Estancia / ubicación"],
  ["causa_probable", "Causa probable"],
  ["elementos_danados", "Elementos dañados"],
  ["intervencion_urgente", "Intervención urgente"],
  ["afectaciones_terceros", "Daños a terceros"],
  ["direccion_siniestro", "Dirección"],
  ["fecha_siniestro", "Fecha del siniestro"],
  ["valoracion_aprox", "Valoración aprox."],
  ["compania_aseguradora", "Compañía"],
  ["poliza", "Póliza"],
  ["observaciones", "Observaciones"],
];

export function AiNotesPanel({ notes, loading, updatedAt, onRefresh, onEditField, fullHeight }) {
  const hasData = notes && AI_NOTES_LABELS.some(([k]) => notes[k]);
  const editedSet = new Set((notes && notes._edited_fields) || []);
  const fmtAt = updatedAt ? new Date(updatedAt).toLocaleTimeString("es-ES", { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit" }) : null;
  const [editing, setEditing] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const commit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await onEditField(editing.key, editing.draft);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`bg-white/[0.04] border border-white/10 rounded-[14px] flex flex-col ${fullHeight ? "h-full" : ""}`}
      style={fullHeight ? undefined : { maxHeight: "32vh" }}
      data-testid="ai-notes-panel"
    >
      <div className="px-4 py-3 border-b border-white/10 text-sm font-medium text-white/85 flex items-center gap-2">
        <Brain className="h-3.5 w-3.5 text-[#D85A30]" /> Notas IA · riesgos diversos
        {fmtAt && <span className="ml-auto text-[10px] text-white/45 font-mono">act. {fmtAt}</span>}
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-white/60 hover:text-white disabled:opacity-50 transition-colors"
          title="Refrescar análisis IA"
          data-testid="ai-notes-refresh-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </button>
      </div>
      <div className="overflow-y-auto vp-scroll p-3 text-sm">
        {!hasData ? (
          <div className="text-xs text-white/55 text-center py-4">
            A medida que la conversación avance, la IA identificará tipo de siniestro, daños, causa, dirección…<br />
            <span className="text-[10px] text-white/40">Haz clic en cualquier campo para editarlo manualmente.</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            {AI_NOTES_LABELS.map(([key, label]) => {
              const v = notes[key];
              const isEditing = editing?.key === key;
              const isEdited = editedSet.has(key);
              if (!v && !isEditing) {
                return (
                  <button
                    key={key}
                    onClick={() => setEditing({ key, draft: "" })}
                    className="block w-full text-left text-[11px] text-white/35 hover:text-white/65 transition-colors py-0.5"
                    data-testid={`ai-note-empty-${key}`}
                  >+ {label}</button>
                );
              }
              return (
                <div key={key} className="vp-fade-in flex gap-2 text-[12px] group" data-testid={`ai-note-${key}`}>
                  <span className="text-white/55 shrink-0 w-[110px] pt-0.5">{label}</span>
                  {isEditing ? (
                    <div className="flex-1 flex gap-1 items-start">
                      <input
                        autoFocus
                        value={editing.draft}
                        onChange={(e) => setEditing({ key, draft: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commit();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        onBlur={commit}
                        className="flex-1 bg-white/10 text-white text-[12px] px-1.5 py-0.5 rounded outline-none border border-white/20 focus:border-[#D85A30]"
                        data-testid={`ai-note-input-${key}`}
                      />
                      {saving && <Loader2 className="h-3 w-3 animate-spin text-white/60 mt-1" />}
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditing({ key, draft: v || "" })}
                      className="flex-1 text-left text-white/95 break-words hover:bg-white/5 px-1 rounded transition-colors"
                      title="Clic para editar"
                    >
                      {v}
                      {isEdited && <span className="ml-1 text-[9px] text-[#5BCBA0] font-mono opacity-80" title="Editado manualmente — el polling automático no lo sobrescribirá">·editado</span>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
